import { GroupEntity, UserEntity } from '@backstage/catalog-model';

export type TeamMemberRole = 'manager' | 'member';

export type TeamMember = {
  entityRef: string;
  name: string;
  displayName: string;
  catalogPath: string;
  role: TeamMemberRole;
};

export function parseGroupMembershipRef(ref: unknown): string | undefined {
  if (typeof ref !== 'string') {
    return undefined;
  }

  const trimmed = ref.trim();
  if (!trimmed) {
    return undefined;
  }

  if (trimmed.toLowerCase().startsWith('group:')) {
    const namePart = trimmed.split(':')[1];
    return namePart?.split('/').pop()?.trim() || undefined;
  }

  if (trimmed.includes(':')) {
    return undefined;
  }

  return trimmed;
}

export function parseUserMembershipRef(ref: unknown): string | undefined {
  if (typeof ref !== 'string') {
    return undefined;
  }

  const trimmed = ref.trim();
  if (!trimmed.toLowerCase().startsWith('user:')) {
    return undefined;
  }

  const namePart = trimmed.split(':')[1];
  return namePart?.split('/').pop()?.trim() || undefined;
}

export function parseManagerUserName(manager: unknown): string | undefined {
  if (typeof manager !== 'string') {
    return undefined;
  }

  const trimmed = manager.trim();
  if (!trimmed) {
    return undefined;
  }

  if (trimmed.toLowerCase().startsWith('user:')) {
    const namePart = trimmed.split(':')[1];
    return namePart?.split('/').pop()?.trim() || undefined;
  }

  if (trimmed.includes(':')) {
    return undefined;
  }

  return trimmed;
}

export function userDisplayName(user: UserEntity): string {
  return user.spec?.profile?.displayName ?? user.metadata.title ?? user.metadata.name;
}

function memberFromUser(user: UserEntity, role: TeamMemberRole = 'member'): TeamMember {
  const namespace = user.metadata.namespace ?? 'default';
  return {
    entityRef: `user:${namespace}/${user.metadata.name}`,
    name: user.metadata.name,
    displayName: userDisplayName(user),
    catalogPath: `/catalog/${namespace}/user/${user.metadata.name}`,
    role,
  };
}

function fallbackMember(userName: string, role: TeamMemberRole = 'member'): TeamMember {
  return {
    entityRef: `user:default/${userName}`,
    name: userName,
    displayName: userName.replace(/\./g, ' '),
    catalogPath: `/catalog/default/user/${userName}`,
    role,
  };
}

export function buildMembersByGroup(
  users: UserEntity[],
  groups: GroupEntity[],
): Map<string, TeamMember[]> {
  const membersByGroup = new Map<string, Map<string, TeamMember>>();
  const usersByName = new Map(users.map(user => [user.metadata.name, user]));

  const addMember = (groupName: string, member: TeamMember) => {
    const bucket = membersByGroup.get(groupName) ?? new Map<string, TeamMember>();
    bucket.set(member.entityRef, member);
    membersByGroup.set(groupName, bucket);
  };

  for (const user of users) {
    const member = memberFromUser(user);
    const memberOf = user.spec?.memberOf;
    if (!Array.isArray(memberOf)) {
      continue;
    }

    for (const groupRef of memberOf) {
      const groupName = parseGroupMembershipRef(groupRef);
      if (groupName) {
        addMember(groupName, member);
      }
    }
  }

  for (const group of groups) {
    const specMembers = (group.spec as { members?: string[] }).members;
    if (!Array.isArray(specMembers)) {
      continue;
    }

    for (const memberRef of specMembers) {
      const userName = parseUserMembershipRef(memberRef);
      if (!userName) {
        continue;
      }

      const user = usersByName.get(userName);
      addMember(group.metadata.name, user ? memberFromUser(user) : fallbackMember(userName));
    }
  }

  for (const group of groups) {
    const managerName = parseManagerUserName(
      (group.spec as { manager?: string }).manager,
    );
    if (!managerName) {
      continue;
    }

    const bucket = membersByGroup.get(group.metadata.name) ?? new Map<string, TeamMember>();
    if (!Array.from(bucket.values()).some(member => member.name === managerName)) {
      const user = usersByName.get(managerName);
      const managerMember = user
        ? memberFromUser(user, 'manager')
        : fallbackMember(managerName, 'manager');
      bucket.set(managerMember.entityRef, managerMember);
      membersByGroup.set(group.metadata.name, bucket);
    }
  }

  const result = new Map<string, TeamMember[]>();
  for (const [groupName, memberMap] of membersByGroup.entries()) {
    const group = groups.find(item => item.metadata.name === groupName);
    const managerName = group
      ? parseManagerUserName((group.spec as { manager?: string }).manager)
      : undefined;

    result.set(
      groupName,
      Array.from(memberMap.values())
        .map(member => ({
          ...member,
          role: member.name === managerName ? ('manager' as const) : ('member' as const),
        }))
        .sort((a, b) => {
          if (a.role !== b.role) {
            return a.role === 'manager' ? -1 : 1;
          }
          return a.displayName.localeCompare(b.displayName, undefined, { sensitivity: 'base' });
        }),
    );
  }

  return result;
}
